import React from 'react';
import { Modal, Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReserveMemberResultList from '../groupLesson/groupLessonTemplate/ReserveMemberResultList';

export const Container = styled(Box, { shouldForwardProp: p => p !== 'wide' })(({ wide }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: wide ? '480px' : '400px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  overflow: 'hidden',
  outline: 'none',
}));

export const Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: '48px',
  padding: '0 24px',
  backgroundColor: '#262626',
});

export const Title = styled(Typography)({ fontSize: '16px', fontWeight: 600, color: '#fff' });

export const Body = styled(Box)({ padding: '24px', backgroundColor: '#fafbfc' });

export const Message = styled(Typography)({
  fontSize: '14px',
  lineHeight: 1.6,
  color: '#4D4D4D',
  whiteSpace: 'pre-line',
});

export const Note = styled(Typography)({ fontSize: '12.5px', lineHeight: 1.6, color: '#8A939C', marginTop: '10px' });

const TargetList = styled(Box)({
  marginTop: '12px',
  border: '1px solid #E6ECEE',
  borderRadius: '6px',
  backgroundColor: '#fff',
  overflow: 'hidden',
});

const TargetRow = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  padding: '8px 12px',
  borderBottom: '1px solid #F0F3F4',
  '&:last-of-type': { borderBottom: 'none' },
});

const TargetMember = styled(Typography)({ fontSize: '13.5px', fontWeight: 700, color: '#262626' });

const TargetTicket = styled(Typography)({
  flex: 1,
  fontSize: '13px',
  color: '#4D4D4D',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const TargetTrainer = styled(Typography)({ fontSize: '12px', color: '#8A939C', whiteSpace: 'nowrap' });

const Footer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '0 24px 20px',
  backgroundColor: '#fafbfc',
});

const PrimaryButton = styled(Button)({
  height: '44px',
  borderRadius: '8px',
  backgroundColor: '#4ab3bc',
  color: '#fff',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: '#3a9aa2' },
});

const SecondaryButton = styled(Button)({
  height: '44px',
  borderRadius: '8px',
  backgroundColor: '#fff',
  border: '1px solid #4ab3bc',
  color: '#2E8A90',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: '#F1FAFB' },
});

export const CancelButton = styled(Button)({
  height: '40px',
  borderRadius: '8px',
  backgroundColor: '#E6E6E6',
  color: '#666',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
});

/**
 * 그룹수업 붙여넣기 — 일정만 복사할지, 예약한 회원까지 복사할지 고른다(#143).
 * 회원까지면 먼저 미리보기(GroupPastePreviewModal)로 예약되지 않을 회원을 보여준다. 대기자는 제외.
 */
export const GroupPasteOptionModal = ({
  open,
  targetLabel,
  memberCount = 0,
  capacity,
  loading,
  onSelect,
  onClose,
}) => {
  const overCapacity = capacity != null && memberCount > capacity;

  return (
    <Modal open={open} onClose={loading ? undefined : onClose}>
      <Container>
        <Header>
          <Title>그룹수업 붙여넣기</Title>
        </Header>
        <Body>
          <Message>{`${targetLabel}에 이 수업을 붙여넣습니다.\n예약한 회원까지 함께 옮길까요?`}</Message>
          <Note>
            {memberCount === 0
              ? '원본 회차에 옮길 수 있는 예약 회원이 없습니다. 일정만 붙여넣습니다.'
              : overCapacity
                ? `예약 회원 ${memberCount}명이 수업 정원 ${capacity}명을 넘어 회원까지는 붙여넣을 수 없습니다. 일정만 만든 뒤 참석관리에서 회원을 넣어 주세요.`
                : `회원까지 고르면 원본 회차의 회원 ${memberCount}명(출석·결석 처리분 포함, 대기·탈퇴 제외)을 새 회차에 예약중으로 넣습니다(지난 시간으로는 붙여넣을 수 없습니다). 붙이기 전에 예약되지 않을 회원과 사유를 먼저 보여줍니다.`}
          </Note>
        </Body>
        <Footer>
          <PrimaryButton disabled={loading || memberCount === 0 || overCapacity} onClick={() => onSelect(true)}>
            {memberCount > 0 ? `예약한 회원 ${memberCount}명까지 붙여넣기` : '예약한 회원까지 붙여넣기'}
          </PrimaryButton>
          <SecondaryButton disabled={loading} onClick={() => onSelect(false)}>
            일정만 붙여넣기
          </SecondaryButton>
          <CancelButton disabled={loading} onClick={onClose}>
            취소
          </CancelButton>
        </Footer>
      </Container>
    </Modal>
  );
};

/**
 * 개인·듀엣 붙여넣기 — 대상 강사가 회원 수강권의 담당강사가 아닐 때(#143).
 * 담당강사는 복수로 둘 수 있으므로 추가가 기본이고, 추가 없이 수업만 만들 수도 있다.
 */
export const TrainerAssignConfirmModal = ({ open, trainerName, targets = [], loading, onSelect, onClose }) => {
  const rows = targets.filter(target => target.needsAssign);

  return (
    <Modal open={open} onClose={loading ? undefined : onClose}>
      <Container>
        <Header>
          <Title>담당강사 확인</Title>
        </Header>
        <Body>
          <Message>{`${trainerName || '이 강사'} 강사는 아직 담당강사가 아닙니다.`}</Message>
          <TargetList>
            {rows.map(target => (
              <TargetRow key={target.memberCourseTicketId || target.memberId}>
                <TargetMember>{target.memberName || target.memberId}</TargetMember>
                <TargetTicket>{target.ticketName || '수강권'}</TargetTicket>
                <TargetTrainer>현재 {target.assignedTrainerNames || '없음'}</TargetTrainer>
              </TargetRow>
            ))}
          </TargetList>
          <Note>기존 담당강사는 그대로 두고 추가합니다. 추가하지 않으면 강사앱 담당회원에 안 보입니다.</Note>
        </Body>
        <Footer>
          <PrimaryButton disabled={loading} onClick={() => onSelect(true)}>
            추가하고 붙여넣기
          </PrimaryButton>
          <SecondaryButton disabled={loading} onClick={() => onSelect(false)}>
            추가 없이 붙여넣기
          </SecondaryButton>
          <CancelButton disabled={loading} onClick={onClose}>
            취소
          </CancelButton>
        </Footer>
      </Container>
    </Modal>
  );
};

/**
 * 일간 드래그로 개인·듀엣·OT 를 다른 강사 컬럼으로 이동할 때 — 새 강사가 회원 수강권의 담당강사가 아닌 경우.
 * onSelect 는 'ADD'(추가) · 'REPLACE'(이동 전 강사만 빼고 추가) · null(담당강사 그대로)을 받는다.
 */
export const MoveTrainerAssignModal = ({ open, fromTrainerName, trainerName, targets = [], loading, onSelect, onClose }) => {
  const rows = targets.filter(target => target.needsAssign);
  const toName = trainerName || '새 강사';
  const fromName = fromTrainerName || '이동 전 강사';

  return (
    <Modal open={open} onClose={loading ? undefined : onClose}>
      <Container wide>
        <Header>
          <Title>담당강사 확인</Title>
        </Header>
        <Body>
          <Message>{`${toName} 강사는 아직 담당강사가 아닙니다.`}</Message>
          <TargetList>
            {rows.map(target => (
              <TargetRow key={target.memberCourseTicketId || target.memberTrialTicketId || target.memberId}>
                <TargetMember>{target.memberName || target.memberId}</TargetMember>
                <TargetTicket>{target.ticketName || '수강권'}</TargetTicket>
                <TargetTrainer>현재 {target.assignedTrainerNames || '없음'}</TargetTrainer>
              </TargetRow>
            ))}
          </TargetList>
          <Note sx={{ whiteSpace: 'pre-line' }}>
            {`추가는 기존 담당강사를 그대로 두고 ${toName} 강사를 더합니다.\n교체는 ${fromName} 강사만 빼고 ${toName} 강사를 넣습니다. 다른 담당강사는 그대로입니다.\n그대로 이동하면 담당강사는 바뀌지 않아 강사앱 담당회원에 안 보입니다.`}
          </Note>
        </Body>
        <Footer>
          <PrimaryButton disabled={loading} onClick={() => onSelect('ADD')}>
            추가하고 이동
          </PrimaryButton>
          <SecondaryButton disabled={loading} onClick={() => onSelect('REPLACE')}>
            교체하고 이동
          </SecondaryButton>
          <SecondaryButton disabled={loading} onClick={() => onSelect(null)}>
            그대로 이동
          </SecondaryButton>
          <CancelButton disabled={loading} onClick={onClose}>
            취소
          </CancelButton>
        </Footer>
      </Container>
    </Modal>
  );
};

const CARRY_STATUS_LABEL = {
  ATTENDED: { text: '출석', color: '#0F7F85', bg: '#E4F5F6' },
  ABSENT: { text: '결석', color: '#C2403D', bg: '#FDF0EF' },
  RESERVED: { text: '예약중', color: '#8A939C', bg: '#EEF1F3' },
};

const SectionTitle = styled(Typography)({ fontSize: '13px', fontWeight: 700, color: '#262626', marginTop: '14px' });

/**
 * 그룹 회원까지 붙여넣기 전 확인 — 새 회차 날짜 기준 판정으로 예약 가능 인원과 제외 회원·사유를 보여준다.
 */
export const GroupPastePreviewModal = ({
  open,
  targetLabel,
  results = [],
  loading,
  onConfirm,
  onClose,
}) => {
  const excluded = results.filter(member => member.failures?.length > 0);
  const reservable = results.filter(member => !(member.failures?.length > 0));

  return (
    <Modal open={open} onClose={loading ? undefined : onClose}>
      <Container wide>
        <Header>
          <Title>회원까지 붙여넣기 확인</Title>
        </Header>
        <Body sx={{ maxHeight: '480px', overflowY: 'auto' }}>
          <Message>{`${targetLabel}에 붙여넣습니다.\n예약 가능 ${reservable.length}명 · 제외 ${excluded.length}명`}</Message>
          {excluded.length > 0 && (
            <>
              <SectionTitle>예약되지 않을 회원</SectionTitle>
              <TargetList>
                <ReserveMemberResultList members={excluded} />
              </TargetList>
            </>
          )}
        </Body>
        <Footer>
          <PrimaryButton disabled={loading} onClick={onConfirm}>
            {excluded.length > 0 ? '그래도 붙여넣기' : '붙여넣기'}
          </PrimaryButton>
          <CancelButton disabled={loading} onClick={onClose}>
            취소
          </CancelButton>
        </Footer>
      </Container>
    </Modal>
  );
};

/** 그룹 회원까지 붙여넣은 뒤, 빠진 회원이 미리보기와 다를 때(그 사이 바뀐 경우)만 사유를 보여준다. */
export const GroupPasteResultModal = ({ open, members = [], onClose }) => (
  <Modal open={open} onClose={onClose}>
    <Container wide>
      <Header>
        <Title>예약되지 않은 회원</Title>
      </Header>
      <Box sx={{ maxHeight: '360px', overflowY: 'auto', backgroundColor: '#fff' }}>
        <ReserveMemberResultList members={members} />
      </Box>
      <Footer sx={{ pt: '16px' }}>
        <CancelButton onClick={onClose}>닫기</CancelButton>
      </Footer>
    </Container>
  </Modal>
);
